/** Parse yyyy-MM-dd to local Date (no timezone shift). */
export function isoToDate(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(String(iso))) return null;
  const [year, month, day] = String(iso).split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Parse ISO / datetime-local (`yyyy-MM-dd` or `yyyy-MM-ddTHH:mm`) to local Date. */
export function isoToDateTime(value) {
  if (!value) return null;
  const raw = String(value).trim();
  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/,
  );
  if (!match) return isoToDate(raw);
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4] || 0),
    Number(match[5] || 0),
    Number(match[6] || 0),
  );
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Format Date to yyyy-MM-ddTHH:mm for datetime-local form state. */
export function dateToIsoDateTime(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

/** Format Date to yyyy-MM-dd for form state / API. */
export function dateToIso(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Display yyyy-MM-dd as dd/MM/yyyy. */
export function formatIsoAsDisplay(iso) {
  const date = isoToDate(iso);
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/** Auto-format digits as dd/mm/yyyy while typing. */
export function formatDisplayInputWhileTyping(input) {
  const digits = String(input || "").replace(/\D/g, "").slice(0, 8);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** Parse dd/MM/yyyy or dd-MM-yyyy typed input to yyyy-MM-dd. */
export function parseDisplayToIso(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const match = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!match) return "";

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
  ) {
    return "";
  }

  return dateToIso(date);
}

/**
 * Coerce Date | yyyy-MM-dd | dd/MM/yyyy into yyyy-MM-dd (or "").
 *
 * Needed because calendar widgets can leave a *display* string in form state
 * after clear-and-type; lexicographic compares against yyyy-MM-dd then silently
 * give the wrong answer. See IpdDateField.
 */
export function normalizeToIsoDate(value) {
  if (value == null || value === "") return "";
  if (value instanceof Date) return dateToIso(value);
  return parseDisplayToIso(value);
}

/** Compare calendar days only (local), for minDate/maxDate checks. */
export function startOfLocalDay(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** True when iso (yyyy-MM-dd) is inside inclusive minDate/maxDate bounds. */
export function isIsoDateInRange(iso, minDate, maxDate) {
  const day = startOfLocalDay(isoToDate(iso));
  if (!day) return false;
  const min = startOfLocalDay(minDate);
  const max = startOfLocalDay(maxDate);
  if (min && day < min) return false;
  if (max && day > max) return false;
  return true;
}
